
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IIdentityIntegrationOidcConfigurationUpdate,
  OIDC_DEFAULT_SCOPES,
  OIDC_TOKEN_AUTH_CLIENT_SECRET_BASIC,
  OIDC_TOKEN_AUTH_METHODS,
} from '@/services/identityIntegrationsService';

const MAPPED_FIELDS = ['issuer', 'discovery_url', 'client_id', 'client_secret', 'token_endpoint_auth_method', 'scopes'];

const ISSUER_CHANGE_REJECTED = 'oidcIssuerChangeRejected';

/**
 * Where an OIDC Integration's users are sent to sign in, and as which client
 * — sp-identity ADR-0017. Nothing here names a provider: an issuer, a client
 * and a set of scopes is all a standards-compliant one needs; its endpoints
 * and signing keys come from its Discovery document at login time.
 */
@Component({ name: 'OidcProviderCard' })
export default class OidcProviderCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected issuer: string = '';
  protected discoveryUrl: string = '';
  protected clientId: string = '';
  protected clientSecret: string = '';
  protected tokenEndpointAuthMethod: string = OIDC_TOKEN_AUTH_CLIENT_SECRET_BASIC;
  protected scopes: string = '';
  protected busy: boolean = false;
  protected confirmingIssuerChange: boolean = false;
  protected errors: IApiFieldError[] = [];

  created() {
    this.reset();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.reset();
  }

  get secretConfigured(): boolean {
    return !!(this.integration.oidc && this.integration.oidc.client_secret_configured);
  }

  get callbackPath(): string {
    return '/h2/oidc/callback';
  }

  get loginPath(): string {
    return '/h2/oidc/' + this.integration.uuid + '/login';
  }

  get authMethodItems() {
    return OIDC_TOKEN_AUTH_METHODS.map((method) => ({
      text: this.$t('identityIntegrations.oidc.authMethods.' + method),
      value: method,
    }));
  }

  // The common shape mistakes, caught before the round trip and keeping Save
  // disabled. The server applies the full rules and stays the authority.
  get issuerRules() {
    return [
      (v: string) => !v || /^https:\/\/[^\s?#]+$/.test(v.trim()) || (this.$t('identityIntegrations.oidc.issuerInvalid') as string),
    ];
  }

  get discoveryRules() {
    return [
      (v: string) => !v || /^https:\/\/[^\s#]+$/.test(v.trim()) || (this.$t('identityIntegrations.oidc.discoveryUrlInvalid') as string),
    ];
  }

  get valid(): boolean {
    const passes = (rules: Array<(v: string) => boolean | string>, value: string) => rules.every((rule) => rule(value) === true);

    return this.issuer.trim() !== ''
      && this.clientId.trim() !== ''
      && passes(this.issuerRules, this.issuer)
      && passes(this.discoveryRules, this.discoveryUrl)
      && passes(this.scopeRules, this.scopes);
  }

  get scopeRules() {
    return [
      (v: string) => this.scopeList(v).indexOf('openid') !== -1 || (this.$t('identityIntegrations.oidc.openidRequired') as string),
    ];
  }

  get dirty(): boolean {
    const current = this.integration.oidc;

    if (this.clientSecret.trim() !== '') {
      return true;
    }

    if (!current) {
      return this.issuer.trim() !== '' || this.clientId.trim() !== '';
    }

    return (
      this.issuer.trim() !== current.issuer ||
      this.normalized(this.discoveryUrl) !== current.discovery_url ||
      this.clientId.trim() !== current.client_id ||
      this.tokenEndpointAuthMethod !== current.token_endpoint_auth_method ||
      this.scopeList(this.scopes).join(' ') !== current.scopes.join(' ')
    );
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors.filter((each) => each.title !== ISSUER_CHANGE_REJECTED), MAPPED_FIELDS);
  }

  fieldError(field: string): string[] {
    return fieldErrorMessages(this, this.errors, field);
  }

  reset() {
    const current = this.integration.oidc;

    this.issuer = current ? current.issuer : '';
    this.discoveryUrl = current && current.discovery_url ? current.discovery_url : '';
    this.clientId = current ? current.client_id : '';
    this.clientSecret = '';
    this.tokenEndpointAuthMethod = current ? current.token_endpoint_auth_method : OIDC_TOKEN_AUTH_CLIENT_SECRET_BASIC;
    this.scopes = (current ? current.scopes : OIDC_DEFAULT_SCOPES).join(' ');
    this.errors = [];
    this.confirmingIssuerChange = false;
  }

  async submit(confirmed: boolean = false) {
    const payload: IIdentityIntegrationOidcConfigurationUpdate = {
      issuer: this.issuer.trim(),
      discovery_url: this.normalized(this.discoveryUrl),
      client_id: this.clientId.trim(),
      token_endpoint_auth_method: this.tokenEndpointAuthMethod,
      scopes: this.scopeList(this.scopes),
    };

    if (this.clientSecret.trim() !== '') {
      payload.client_secret = this.clientSecret;
    }

    if (confirmed) {
      payload.confirm_issuer_change = true;
    }

    this.busy = true;
    this.errors = [];
    try {
      const updated = await identityIntegrationsService.replaceOidcConfiguration(this.integration.id, payload);
      this.confirmingIssuerChange = false;
      this.clientSecret = '';
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.errors = collectErrors(error);
      // A deliberate-override refusal, not an error: ask, then resend.
      this.confirmingIssuerChange = this.errors.some((each) => each.title === ISSUER_CHANGE_REJECTED);
    } finally {
      this.busy = false;
    }
  }

  private scopeList(value: string): string[] {
    const seen: string[] = [];

    (value || '').split(/\s+/).forEach((scope) => {
      if (scope !== '' && seen.indexOf(scope) === -1) {
        seen.push(scope);
      }
    });

    return seen;
  }

  private normalized(value: string): string | null {
    const trimmed = (value || '').trim();
    return trimmed === '' ? null : trimmed;
  }
}
